import { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';
import { Shipment } from '@/types/shipment.types';
import {
  LabelType,
  StandardLabel,
  CompactLabel,
  ThermalLabel,
  DetailedLabel,
  MinimalLabel,
} from './PrintLabelDialog';

interface ExtendedShipment extends Shipment {
  tenant?: { name: string };
}

interface BulkPrintLabelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipments: ExtendedShipment[];
  labelType: LabelType;
}

function renderLabel(shipment: ExtendedShipment, labelType: LabelType) {
  switch (labelType) {
    case 'standard':
      return <StandardLabel shipment={shipment} />;
    case 'compact':
      return <CompactLabel shipment={shipment} />;
    case 'thermal':
      return <ThermalLabel shipment={shipment} />;
    case 'detailed':
      return <DetailedLabel shipment={shipment} />;
    case 'minimal':
      return <MinimalLabel shipment={shipment} />;
  }
}

export function BulkPrintLabelDialog({
  open,
  onOpenChange,
  shipments,
  labelType,
}: BulkPrintLabelDialogProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
        <head>
          <title>طباعة ${shipments.length} ملصق</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              direction: rtl;
              padding: 10mm;
            }
            .label-page {
              page-break-after: always;
              break-after: page;
            }
            .label-page:last-child {
              page-break-after: auto;
              break-after: auto;
            }
            @media print {
              body { padding: 0; }
              @page { margin: 5mm; }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>معاينة الملصقات ({shipments.length})</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-white rounded-lg border p-4 overflow-auto max-h-[60vh]" dir="rtl">
            <div ref={printRef}>
              {shipments.map((shipment) => (
                <div
                  key={shipment.id}
                  className="label-page"
                  style={{ marginBottom: '8mm', display: 'flex', justifyContent: 'center' }}
                >
                  {renderLabel(shipment, labelType)}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              إغلاق
            </Button>
            <Button onClick={handlePrint} className="gap-2">
              <Printer className="h-4 w-4" />
              طباعة {shipments.length} ملصق
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
